<template>
    <div v-if="!!model">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.ocupacoes"
                            @change="handleOcupacaoChange"
                            default-option-text="Todas"
                            label="CBO"
                            show-default-option
                            text-field="nome"
                            v-model="model.ocupacaoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.especialidades"
                            @change="handleEspecialidadeChange"
                            default-option-text="Todas"
                            label="Especialidade"
                            show-default-option
                            text-field="nome"
                            v-model="model.especialidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.usuarios"
                            @change="handleUsuarioChange"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.status"
                            @change="handleStatusChange"
                            default-option-text="Todos"
                            label="Status"
                            show-default-option
                            text-field="nome"
                            v-model="model.statusId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.usuariosCancelamento"
                            default-option-text="Todos"
                            label="Usuário responsável pelo cancelamento"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioCancelamentoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-input-autocomplete
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :repository="repositories.relatorioCancelamento"
                            :request-query-string-params="requestQueryStringParams"
                            :search-fields="pacienteSearchFields"
                            :text-field="p => `${p.nome}${p.nomeSocial ? ` (${p.nomeSocial})` : ''}${p.cns ? ` - ${p.cns}` : ''}`"
                            label="Paciente"
                            placeholder="Nome, Nome social, CNS, CPF, Data de nascimento ou Prontuário"
                            v-model="model.paciente"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <template v-if="itens.length > 0">
            <rkm-area
                no-margin-top
                title="Resultado"
            >
                <template v-slot:trailing>
                    <rkm-dropdown
                        class="mr-2"
                        color="primary"
                        cols="1"
                        menu-class="auto-scroll"
                        no-caret
                        small
                        title="Colunas"
                        v-rkm-tooltip.hover
                    >
                        <template v-slot:button-content>
                            <rkm-icon icon="columns" />
                        </template>

                        <rkm-form-checkbox
                            :indeterminate="indeterminate"
                            @change="selectAll"
                            class="mx-2"
                            v-model="selectedAll"
                        >
                            Todas
                        </rkm-form-checkbox>

                        <rkm-form-checkbox-group
                            :options="optionalColumns"
                            class="mx-2"
                            stacked
                            text-field="label"
                            v-model="selectedCols"
                            value-field="key"
                        />
                    </rkm-dropdown>

                    <rkm-button
                        @click="print"
                        color="info"
                        small
                        title="Imprimir"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>

                    <rkm-button
                        @click="exportTo"
                        color="info"
                        small
                        title="Exportar"
                    >
                        <rkm-icon icon="file-export" />
                    </rkm-button>
                </template>

                <rkm-table
                    :fields="fields"
                    :items="itens"
                    :sort-by="sortBy"
                    :sort-compare="sortCompare"
                    :sort-desc="sortDesc"
                    @sort-changed="onSortChanged"
                />
            </rkm-area>

            <rkm-area
                no-margin-top
                title="Totalizadores"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="totalizadores.canceladoPaciente"
                            disabled
                            label="Cancelado pelo paciente"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="totalizadores.canceladoProfissional"
                            disabled
                            label="Cancelado pelo profissional"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="totalizadores.total"
                            disabled
                            label="Total geral"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </template>
    </div>
</template>

<script>
// Mixins.
import { data, error, file, object, repository, stringCase } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        validations() {
            return {
                periodo: { required: true },
            }
        },
        optionalColumns() {
            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {
            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        pacienteSearchFields() {
            return [
                { key: 'cadastro_cidadao.nome', searchable: true },
                { key: 'cadastro_cidadao.nomeSocial', searchable: true },
                { key: 'cadastro_cidadao.cns', searchable: true },
                { key: 'cadastro_cidadao.cpf', searchable: true },
                { key: 'cadastro_cidadao.data_nascimento', searchable: true },
                { key: 'cadastro_paciente.prontuario', searchable: true },
            ]
        },
        requestQueryStringParams() {
            return this.camelObjectKeysToSnakeRecursive({
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                ocupacaoId: this.model.ocupacaoId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: this.model.usuarioId,
                statusId: this.model.statusId,
                usuarioCancelamentoId: this.model.usuarioCancelamentoId,
            })
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.ambulatorio'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
            relatorioCancelamento: this.getConectaSusRepository('ambulatorio.relatorioCancelamento'),
        }
    },
    data() {
        return {
            additionals: null,
            columns: [
                { fixed: true, key: 'paciente', label: 'Paciente', sortable: true },
                { fixed: true, key: 'status', label: 'Status', sortable: true },
                { fixed: true, key: 'dataHoraCancelamento', label: 'Data e hora do cancelamento', sortable: true },
                { fixed: true, key: 'usuarioCancelamento', label: 'Usuário responsável pelo cancelamento', sortable: true },
                { fixed: true, key: 'motivoCancelamento', label: 'Motivo do cancelamento', sortable: true },
                { key: 'prontuario', label: 'Prontuário', sortable: true },
                { key: 'dataNascimento', label: 'Data de nascimento', sortable: true },
                { key: 'cpf', label: 'CPF', sortable: true },
                { key: 'cns', label: 'CNS', sortable: true },
                { key: 'unidade', label: 'Unidade', sortable: true },
                { key: 'profissional', label: 'Profissional', sortable: true },
                { key: 'especialidade', label: 'Especialidade', sortable: true },
                { key: 'dataHoraAgendamento', label: 'Data e hora do agendamento', sortable: true },
                { key: 'localAtendimento', label: 'Local de atendimento', sortable: true },
                { key: 'cbo', label: 'CBO', sortable: true },
            ],
            form: null,
            indeterminate: true,
            itens: [],
            model: null,
            others: { moment },
            selectedAll: false,
            selectedCols: [],
            sortBy: 'paciente',
            sortDesc: false,
            totalizadores: null,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioCancelamento.create()
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange(value) {
            this.form.infraestruturas = []
            this.form.ocupacoes = []
            this.form.especialidades = []
            this.form.usuarios = []
            this.form.status = []
            this.form.usuariosCancelamento = []

            this.model.infraestruturaId = null
            this.model.ocupacaoId = null
            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.statusId = null
            this.model.usuarioCancelamentoId = null

            this.totalizadores = null
            this.itens = []

            const request = {
                listar: 'infraestruturas',
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeId: value,
            }

            this.setLoading()

            this.repositories.relatorioCancelamento.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                    this.form.ocupacoes = Object.assign(response.data.ocupacoes)
                    this.form.especialidades = Object.assign(response.data.especialidades)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                    this.form.usuariosCancelamento = Object.assign(response.data.usuariosCancelamento)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleInfraestruturaChange(value) {
            this.form.ocupacoes = []
            this.form.especialidades = []
            this.form.usuarios = []
            this.form.status = []
            this.form.usuariosCancelamento = []

            this.model.ocupacaoId = null
            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.statusId = null
            this.model.usuarioCancelamentoId = null

            this.totalizadores = null
            this.itens = []

            const request = {
                listar: 'ocupacoes',
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: value,
            }

            this.setLoading()

            this.repositories.relatorioCancelamento.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.ocupacoes = Object.assign(response.data.ocupacoes)
                    this.form.especialidades = Object.assign(response.data.especialidades)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                    this.form.usuariosCancelamento = Object.assign(response.data.usuariosCancelamento)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleOcupacaoChange(value) {
            this.form.especialidades = []
            this.form.usuarios = []
            this.form.status = []
            this.form.usuariosCancelamento = []

            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.statusId = null
            this.model.usuarioCancelamentoId = null

            this.totalizadores = null
            this.itens = []

            const request = {
                listar: 'especialidades',
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                ocupacaoId: value,
            }

            this.setLoading()

            this.repositories.relatorioCancelamento.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.especialidades = Object.assign(response.data.especialidades)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                    this.form.usuariosCancelamento = Object.assign(response.data.usuariosCancelamento)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleEspecialidadeChange(value) {
            this.form.usuarios = []
            this.form.status = []
            this.form.usuariosCancelamento = []

            this.model.usuarioId = null
            this.model.statusId = null
            this.model.usuarioCancelamentoId = null

            this.totalizadores = null
            this.itens = []

            const request = {
                listar: 'usuarios',
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                ocupacaoId: this.model.ocupacaoId,
                especialidadeId: value,
            }

            this.setLoading()

            this.repositories.relatorioCancelamento.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                    this.form.usuariosCancelamento = Object.assign(response.data.usuariosCancelamento)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUsuarioChange(value) {
            this.form.status = []
            this.form.usuariosCancelamento = []

            this.model.statusId = null
            this.model.usuarioCancelamentoId = null

            this.totalizadores = null
            this.itens = []

            const request = {
                listar: 'status',
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                ocupacaoId: this.model.ocupacaoId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: value,
            }

            this.setLoading()

            this.repositories.relatorioCancelamento.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.status = Object.assign(response.data.status)
                    this.form.usuariosCancelamento = Object.assign(response.data.usuariosCancelamento)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleStatusChange(value) {
            this.form.usuariosCancelamento = []

            this.model.usuarioCancelamentoId = null

            this.totalizadores = null
            this.itens = []

            const request = {
                listar: 'usuariosCancelamento',
                periodo: {
                    start: this.model.periodo.start ? this.others.moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? this.others.moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                ocupacaoId: this.model.ocupacaoId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: this.model.usuarioId,
                statusId: value,
            }

            this.setLoading()

            this.repositories.relatorioCancelamento.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.form.usuariosCancelamento = Object.assign(response.data.usuariosCancelamento))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            this.setLoading()

            this.repositories.relatorioCancelamento.index(this.prepareData())
                .then(response => {
                    this.totalizadores = Object.assign(response.data.totalizadores)
                    this.itens = Object.assign(response.data.itens)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.ocupacoes = []
            this.form.especialidades = []
            this.form.usuarios = []
            this.form.status = []
            this.form.usuariosCancelamento = []

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.ocupacaoId = null
            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.statusId = null
            this.model.usuarioCancelamentoId = null
            this.model.paciente = null

            this.totalizadores = null
            this.itens = []

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {
            this.setLoading()

            this.repositories.impressao.relatorioCancelamento(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            this.repositories.exportacao.relatorioCancelamento(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        selectAll(selected) {
            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
        sortCompare(a, b, key) {
            const dateFormats = {
                dataHoraCancelamento: 'DD/MM/YYYY [às] HH:mm',
                dataNascimento: 'DD/MM/YYYY',
                dataHoraAgendamento: 'DD/MM/YYYY [às] HH:mm',
            }

            if (dateFormats[key]) {
                const dateA = moment(a[key], dateFormats[key])
                const dateB = moment(b[key], dateFormats[key])

                if (dateA > dateB) return 1
                if (dateA < dateB) return -1
                return 0
            }

            return false
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        prepareData() {
            const data = this.clone(this.model)

            data.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            data.sortDesc = this.sortDesc

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        prepareDataWithColumns() {
            const data = this.prepareData()

            data.columns = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
    },
    mixins: [data, error, file, object, repository, stringCase],
    mounted() {
        this.load()
    },
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                this.form.unidades = []
                this.form.infraestruturas = []
                this.form.ocupacoes = []
                this.form.especialidades = []
                this.form.usuarios = []
                this.form.status = []
                this.form.usuariosCancelamento = []

                this.model.unidadeId = null
                this.model.infraestruturaId = null
                this.model.ocupacaoId = null
                this.model.especialidadeId = null
                this.model.usuarioId = null
                this.model.statusId = null
                this.model.usuarioCancelamentoId = null

                this.totalizadores = null
                this.itens = []

                if (value.start === null || value.end === null) {
                    return
                }

                const request = {
                    listar: 'unidades',
                    periodo: {
                        start: this.others.moment(value.start).format('DD/MM/YYYY'),
                        end: this.others.moment(value.end).format('DD/MM/YYYY'),
                    },
                }

                this.setLoading()

                this.repositories.relatorioCancelamento.list(this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        this.form.unidades = Object.assign(response.data.unidades)
                        this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                        this.form.ocupacoes = Object.assign(response.data.ocupacoes)
                        this.form.especialidades = Object.assign(response.data.especialidades)
                        this.form.usuarios = Object.assign(response.data.usuarios)
                        this.form.status = Object.assign(response.data.status)
                        this.form.usuariosCancelamento = Object.assign(response.data.usuariosCancelamento)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            },
        },
    },
}
</script>
